import { useMemo } from 'react';
import { Activity } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { formatUnits } from 'viem';
import { useReadContract, useReadContracts } from 'wagmi';
import { SALT_RWA_ABI } from '../../web3/abis';

const WAREHOUSE_COLORS = ['#22d3a8', '#38bdf8', '#f0b90b', '#c084fc', '#fb923c', '#94a3b8'];

export default function WarehouseComposition({
  tokenAddress,
  chainId,
  totalSupply,
  totalRedeemed,
}: {
  tokenAddress: `0x${string}`;
  chainId: number;
  totalSupply?: bigint;
  totalRedeemed?: bigint;
}) {
  const { data: lotCount } = useReadContract({
    address: tokenAddress,
    abi: SALT_RWA_ABI,
    functionName: 'lotCount',
    chainId,
  });

  const lotContracts = useMemo(() => {
    if (lotCount === undefined) return [];
    return Array.from({ length: Number(lotCount) }, (_, i) => ({
      address: tokenAddress,
      abi: SALT_RWA_ABI,
      functionName: 'lots',
      args: [BigInt(i)],
      chainId,
    }) as const);
  }, [lotCount, tokenAddress, chainId]);

  const { data: lots } = useReadContracts({ contracts: lotContracts, query: { enabled: lotContracts.length > 0 } });

  const slices = useMemo(() => {
    if (!lots) return [];
    return lots
      .map((lot) => {
        if (lot.status !== 'success') return null;
        const [batchId, kg] = lot.result as readonly [string, bigint, string, bigint, string];
        return { name: batchId, kg: Number(kg) };
      })
      .filter((s): s is { name: string; kg: number } => s !== null && s.kg > 0);
  }, [lots]);

  const totalKg = slices.reduce((sum, s) => sum + s.kg, 0);

  const supplyNum = totalSupply !== undefined ? Number(formatUnits(totalSupply, 18)) : 0;
  const redeemedNum = totalRedeemed !== undefined ? Number(formatUnits(totalRedeemed, 18)) : 0;
  const issuedTotal = supplyNum + redeemedNum;
  const circulatingPct = issuedTotal > 0 ? (supplyNum / issuedTotal) * 100 : 100;

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4 flex flex-col">
      <h3 className="text-sm font-semibold text-foam-100 mb-3 flex items-center gap-1.5">
        <Activity size={14} className="text-tide-400" />
        창고별 재고 구성
      </h3>

      {slices.length === 0 ? (
        <div className="text-xs text-foam-500 py-8 text-center flex-1">재고 정보를 불러오는 중...</div>
      ) : (
        <div className="flex items-center gap-4">
          <div className="w-32 h-32 shrink-0 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="kg"
                  nameKey="name"
                  innerRadius={38}
                  outerRadius={60}
                  paddingAngle={2}
                  strokeWidth={0}
                >
                  {slices.map((s, i) => (
                    <Cell key={s.name} fill={WAREHOUSE_COLORS[i % WAREHOUSE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#0d1420', border: '1px solid #182437', borderRadius: 8, fontSize: 12 }}
                  formatter={(value) => [`${Number(value).toLocaleString('en-US')} kg`]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[10px] text-foam-500">총계</span>
              <span className="text-sm font-semibold text-foam-100 tabular-nums">
                {(totalKg / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}t
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            {slices.map((s, i) => (
              <div key={s.name} className="flex items-center gap-2 text-[11px]">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: WAREHOUSE_COLORS[i % WAREHOUSE_COLORS.length] }}
                />
                <span className="text-foam-300 truncate">{s.name}</span>
                <span className="text-foam-500 tabular-nums ml-auto">
                  {totalKg > 0 ? ((s.kg / totalKg) * 100).toFixed(0) : 0}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-abyss-700">
        <div className="flex justify-between text-[11px] mb-1.5">
          <span className="text-tide-400 font-medium">유통 중 {circulatingPct.toFixed(1)}%</span>
          <span className="text-foam-500 font-medium">상환 완료 {(100 - circulatingPct).toFixed(1)}%</span>
        </div>
        <div className="h-2 rounded-full bg-abyss-900 overflow-hidden flex">
          <div className="h-full bg-tide-500 transition-all duration-500" style={{ width: `${circulatingPct}%` }} />
          <div className="h-full bg-amber-500/70 transition-all duration-500" style={{ width: `${100 - circulatingPct}%` }} />
        </div>
      </div>
    </div>
  );
}
